import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers, renderList as _renderList, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, Teleport as _Teleport, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-04a47d40"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "select-multi" }
const _hoisted_2 = { class: "box-label" }
const _hoisted_3 = { class: "box-more" }
const _hoisted_4 = /*#__PURE__*/_createTextVNode("none selected")
const _hoisted_5 = /*#__PURE__*/_createTextVNode(" all selected ")
const _hoisted_6 = ["id", "aria-labelledby", "aria-activedescendant"]
const _hoisted_7 = ["id", "aria-label", "aria-selected", "data-selected", "data-highlighted"]
const _hoisted_8 = { class: "option-icon" }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "option-label truncate" }, "All", -1))
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "option-count" }, null, -1))
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  class: "option-spacer",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_12 = ["id", "aria-selected", "data-selected", "data-highlighted", "onClick", "onMouseenterCapture"]
const _hoisted_13 = { class: "option-label truncate" }
const _hoisted_14 = {
  key: 0,
  class: "option-count"
}

import { ref, computed, watch, nextTick } from "vue";
import { isEqual, uniqueId } from "lodash";
import { Options } from "./AppSelectMulti";
import { wrap } from "@/util/math";
import { useFloating } from "@/util/composables";

interface Props {
  /** two-way bound selected items state */
  modelValue: Options;
  /** name of the field */
  name: string;
  /** list of options to show */
  options: Options;
  /** whether to show count col */
  showCounts?: boolean;
  /** visual design */
  design?: "normal" | "small";
}

interface Emits {
  /** two-way bound selected items state */
  (event: "update:modelValue", value: Options): void;
  /** when value changed */
  (event: "input"): void;
  /** when value change "submitted"/"committed" by user */
  (event: "change", value: Options): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppSelectMulti',
  props: {
    modelValue: null,
    name: null,
    options: null,
    showCounts: { type: Boolean, default: true },
    design: { default: "normal" }
  },
  emits: ["update:modelValue", "input", "change"],
  setup(__props: any, { emit }: { emit: ({
  /** two-way bound selected items state */
  (event: "update:modelValue", value: Options): void;
  /** when value changed */
  (event: "input"): void;
  /** when value change "submitted"/"committed" by user */
  (event: "change", value: Options): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { modelValue: Options, name: string, options: Options, showCounts: boolean, design: "normal" | "small" }





/** unique id for instance of component */
const id = ref(uniqueId());
/** whether dropdown is open */
const expanded = ref(false);
/** array of indices of selected options */
const selected = ref<Array<number>>([]);
/** selected state when dropdown was opened */
const original = ref<Array<number>>([]);
/** index of option that is highlighted */
const highlighted = ref(0);

/** anchor element */
const anchor = ref();
/** dropdown element */
const dropdown = ref();
/** get dropdown position */
const { calculate, style } = useFloating(
  computed(() => anchor.value?.button || anchor.value),
  dropdown
);
/** recompute position after opened */
watch(expanded, async () => {
  await nextTick();
  if (expanded.value) calculate();
});

function open() {
  /** open dropdown */
  expanded.value = true;
  /** auto highlight first selected option */
  highlighted.value = selected.value[0] || 0;
  /** remember selected state when opened */
  original.value = [...selected.value];
}

function close() {
  /** close dropdown */
  expanded.value = false;
  /** emit change, if value is different from when dropdown first opened */
  if (!isEqual(selected.value, original.value)) emit("change", getModel());
  /** also update original value here in case e.g. user presses esc then blurs */
  original.value = [...selected.value];
}

/** when button clicked */
function onClick() {
  /** toggle dropdown */
  expanded.value ? close() : open();
  /** https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus */
  (document.querySelector(`#select-${id.value}`) as HTMLElement)?.focus();
}

/** when button blurred */
function onBlur() {
  close();
}

/** when user presses key on button */
function onKeydown(event: KeyboardEvent) {
  /** arrow/home/end keys */
  if (
    ["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) &&
    expanded.value
  ) {
    /** prevent page scroll */
    event.preventDefault();

    /** move value up/down */
    let index = highlighted.value;
    if (event.key === "ArrowUp") index--;
    if (event.key === "ArrowDown") index++;
    if (event.key === "Home") index = 0;
    if (event.key === "End") index = props.options.length - 1;

    /** update highlighted, wrapping beyond -1 or options length */
    highlighted.value = wrap(index, -1, props.options.length - 1);
  }

  /** enter key to de/select highlighted option */
  if (expanded.value && (event.key === "Enter" || event.key === " ")) {
    /** prevent browser re-clicking open button */
    event.preventDefault();
    toggleSelect(highlighted.value);
  }

  /** esc key to close dropdown */
  if (expanded.value && event.key === "Escape") close();
}

/** get new selected option indices from model value */
function getSelected(): Array<number> {
  return props.options
    .map((option, index) =>
      props.modelValue.find((model) => option.id === model.id) ? index : -1
    )
    .filter((index) => index !== -1);
}

/** get new model value from selected option indices */
function getModel(): Options {
  return props.options.filter((_, index) => selected.value.includes(index));
}

/** select or deselect option(s) */
function toggleSelect(index = -1, shift = false) {
  /** toggle all */
  if (index === -1) {
    if (allSelected.value) selected.value = [];
    else
      selected.value = Array(props.options.length)
        .fill(0)
        .map((_, index) => index);
  } else if (shift) {
    /** solo/un-solo one */
    if (isEqual(selected.value, [index]))
      selected.value = Array(props.options.length)
        .fill(0)
        .map((_, index) => index);
    else selected.value = [index];
  } else {
    /** toggle one */
    if (selected.value.includes(index))
      selected.value = selected.value.filter((value) => value !== index);
    else selected.value.push(index);
  }

  /** keep in order for easy comparison */
  selected.value.sort();
  /** emit input event for listening for only user-originated inputs */
  emit("input");
}

/** when model changes, update selected indices */
watch(
  () => props.modelValue,
  () => {
    /** avoid infinite rerenders */
    if (!isEqual(selected.value, getSelected())) selected.value = getSelected();
  },
  { deep: true, immediate: true }
);

/** when selected index changes, update model */
watch(selected, () => emit("update:modelValue", getModel()), { deep: true });

/** when highlighted index changes */
watch(highlighted, () =>
  /** scroll to highlighted in dropdown */
  document
    .querySelector(`#option-${id.value}-${highlighted.value} > *`)
    ?.scrollIntoView({ block: "nearest" })
);

/** are all options selected */
const allSelected = computed(
  () => selected.value.length === props.options.length
);

return (_ctx: any,_cache: any) => {
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_resolveDynamicComponent(__props.design === 'small' ? 'AppButton' : 'button'), {
      id: `select-${id.value}`,
      ref_key: "anchor",
      ref: anchor,
      class: _normalizeClass(__props.design === 'normal' ? 'box' : ''),
      "aria-label": __props.name,
      "aria-expanded": expanded.value,
      "aria-controls": `list-${id.value}`,
      "aria-haspopup": "listbox",
      "data-notification": !_unref(allSelected),
      icon: "filter",
      design: "small",
      onClick: onClick,
      onKeydown: onKeydown,
      onBlur: onBlur
    }, {
      default: _withCtx(() => [
        (__props.design === 'normal')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("span", _hoisted_2, [
                _createTextVNode(_toDisplayString(__props.name) + " ", 1),
                _createElementVNode("span", _hoisted_3, [
                  (selected.value.length === 0)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _hoisted_4
                      ], 64))
                    : (selected.value.length === __props.options.length)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _hoisted_5
                        ], 64))
                      : (selected.value.length === 1)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            _createTextVNode(_toDisplayString(__props.options[selected.value[0]]?.id), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                            _createTextVNode(_toDisplayString(selected.value.length) + " selected", 1)
                          ], 64))
                ])
              ]),
              _createVNode(_component_AppIcon, {
                class: "button-icon",
                icon: expanded.value ? 'angle-up' : 'angle-down'
              }, null, 8, ["icon"])
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 40, ["id", "class", "aria-label", "aria-expanded", "aria-controls", "data-notification"])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (expanded.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            id: `list-${id.value}`,
            ref_key: "dropdown",
            ref: dropdown,
            class: "list",
            role: "listbox",
            "aria-labelledby": `select-${id.value}`,
            "aria-activedescendant": 
          expanded.value ? `option-${id.value}-${highlighted.value}` : undefined
        ,
            tabindex: "0",
            style: _normalizeStyle(_unref(style))
          }, [
            _createElementVNode("div", {
              id: `option-${id.value}--1`,
              class: "option",
              role: "option",
              "aria-label": _unref(allSelected) ? 'Deselect all' : 'Select all',
              "aria-selected": _unref(allSelected),
              "data-selected": _unref(allSelected),
              "data-highlighted": highlighted.value === -1,
              tabindex: "0",
              onClick: _cache[0] || (_cache[0] = () => toggleSelect(-1)),
              onMouseenter: _cache[1] || (_cache[1] = ($event: any) => (highlighted.value = -1)),
              onMousedown: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
              onFocusin: _cache[3] || (_cache[3] = () => null),
              onKeydown: _cache[4] || (_cache[4] = () => null)
            }, [
              _createElementVNode("span", _hoisted_8, [
                _createVNode(_component_AppIcon, {
                  icon: _unref(allSelected) ? 'square-check' : 'square'
                }, null, 8, ["icon"])
              ]),
              _hoisted_9,
              _hoisted_10
            ], 40, _hoisted_7),
            _hoisted_11,
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option, index) => {
              return _withDirectives((_openBlock(), _createElementBlock("div", {
                id: `option-${id.value}-${index}`,
                key: index,
                class: "option",
                role: "option",
                "aria-selected": selected.value.includes(index),
                "data-selected": selected.value.includes(index),
                "data-highlighted": index === highlighted.value,
                tabindex: "0",
                onClick: (event) => toggleSelect(index, event.shiftKey),
                onMouseenterCapture: ($event: any) => (highlighted.value = index),
                onMousedown: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"])),
                onFocusin: _cache[6] || (_cache[6] = () => null),
                onKeydown: _cache[7] || (_cache[7] = () => null)
              }, [
                _createVNode(_component_AppIcon, {
                  icon: selected.value.includes(index) ? 'square-check' : 'square',
                  class: "option-icon"
                }, null, 8, ["icon"]),
                _createElementVNode("span", _hoisted_13, _toDisplayString(option.name || option.id), 1),
                (__props.showCounts && option.count)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(option.count), 1))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_12)), [
                [_directive_tooltip, option.tooltip]
              ])
            }), 128))
          ], 12, _hoisted_6))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})